/* eslint-disable no-param-reassign */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { makeObservable, action, observable, computed, runInAction } from 'mobx';

import handleError from 'helpers/handleError';
import { toast } from 'shared/components/common/misc/Toast';
import { localize } from 'shared/components/other';
import { BaseStore } from 'shared/stores';
import { EmptyObject, IOption, IOptionMyTarget, IError } from 'types';
import {
  AdvertisementProps,
  FileData,
  FileTypes,
  MediaData,
  MediaTypes,
  OptionData,
  OptionName,
  Options,
  TemplateData,
  TextFieldProps,
  CabinetProps,
} from 'types/cabinetCopying';

import { LocaleIdType } from 'locales';
import { isValidUrl } from 'helpers/isValidUrl';
import { encodeUrlParams } from 'helpers/encodeUrlParams';
import { getFileType } from '../helpers/getFileType';
import { initialFileData, initialOptionConfig, optionNamesByPriority } from '../helpers/config';
import { CabinetCopyingLogStore } from './CabinetCopyingLog.store';

type ChildrenStores = {
  log: CabinetCopyingLogStore;
};

export class CabinetCopyingStore extends BaseStore<EmptyObject, ChildrenStores> {
  @observable
  isPanelActive = true;

  @observable
  options: Options = initialOptionConfig;

  @observable
  optionNames: OptionName[] = optionNamesByPriority;

  @observable
  textFields: TextFieldProps[] = [];

  @observable
  private fileData: FileData = initialFileData;

  @observable
  private mediaData: MediaData = {};

  @observable
  private templateData: TemplateData[] = [];

  private abortController: AbortController | null = null;

  constructor() {
    super();
    makeObservable(this);

    this.childrenStores = {
      log: new CabinetCopyingLogStore(),
    };
  }

  @computed
  get isClientOptional(): boolean {
    return (
      this.options.account.items.find((item) => item.value === this.options.account.value)?.accountType === 'general'
    );
  }

  @computed
  get isValid(): boolean {
    const areOptionValuesValid = Object.entries(this.options).every(([optionName, option]) => {
      const isRedundantOption =
        (optionName === 'client' && this.isClientOptional) || !this.optionNames.includes(optionName as OptionName);
      return option.value !== null || isRedundantOption;
    });

    const areTextValuesValid = this.textFields.every(({ invalid }) => !invalid);

    const isExcelValid = this.fileData.EXCEL !== null;
    const isMediaValid = this.needMediaFile ? this.fileData.MEDIA !== null : true;
    const isLogoValid = this.needLogoFile ? this.fileData.LOGO !== null : true;

    return areOptionValuesValid && areTextValuesValid && isExcelValid && isMediaValid && isLogoValid;
  }

  @computed
  get advertisement(): (IOption & AdvertisementProps) | undefined {
    return this.options.ad.items.find((item) => item.value === this.options.ad.value);
  }

  @computed
  get needMediaFile(): boolean {
    return !!this.advertisement?.allowedMediaTypes.length;
  }

  @computed
  get needLogoFile(): boolean {
    return !!this.advertisement?.requiredLogo;
  }

  @computed
  get templateFileId(): string | null {
    const cabinetCopyingId = this.options.cabinet.value;
    const cabinetId = this.options.cabinetId.value;
    const fileIdByCabinetAccountCopyingId =
      this.templateData.find(
        (item) => item.cabinetAccountCopyingId && item.cabinetAccountCopyingId === cabinetCopyingId,
      )?.fileS3Id ?? null;
    const fileIdByCabinetId = this.templateData.find((item) => item.cabinetId === cabinetId)?.fileS3Id ?? null;
    return fileIdByCabinetAccountCopyingId || fileIdByCabinetId;
  }

  @computed
  private get optionValues(): OptionData {
    const adGroupValue = this.options.adGroup.value ?? this.options.adGroupMyTarget?.value;
    return {
      cabinet: this.options.cabinet.value ?? undefined,
      cabinetId: this.options.cabinetId.value ?? undefined,
      account: this.options.account.value ?? undefined,
      client: this.options.client.value ?? undefined,
      campaign: this.options.campaign.value ?? undefined,
      adGroup: adGroupValue ?? undefined,
      ad: this.options.ad.value ?? undefined,
    };
  }

  @action
  cleanUp = (): void => {
    this.options = initialOptionConfig;
    this.optionNames = optionNamesByPriority;
    this.textFields = [];
    this.fileData = initialFileData;
    this.mediaData = {};
    this.abortController = null;
  };

  @action
  setIsPanelActive = (isActive: boolean): void => {
    this.isPanelActive = isActive;
  };

  @action
  setOption = async (optionName: OptionName, value: number | null): Promise<void> => {
    const selectedCabinet = this.options.cabinet.items.find((item) => item.value === value);

    if (optionName === 'cabinet') {
      this.options = { ...initialOptionConfig, cabinet: this.options.cabinet };
      this.options.cabinet.value = value;
      this.options.cabinetId.value = selectedCabinet?.cabinetId || null;
      await this.loadMetadata();
    } else {
      this.options[optionName].value = value;
    }

    if (value === null) return;

    const optionIndex = this.optionNames.findIndex((name) => name === optionName);
    let nextOptionName = null;
    const hasValidIndex = optionIndex > -1;
    const hasNextOption = optionIndex < this.optionNames.length - 1;

    if (hasValidIndex && hasNextOption) {
      nextOptionName = this.optionNames[optionIndex + 1];
    }

    if (nextOptionName) {
      await this.loadOptionItems(nextOptionName);
    }
  };

  @action
  setTextField = (fieldName: string, value: string): void => {
    this.textFields.forEach((textField) => {
      if (textField.name === fieldName) {
        textField.value = value;

        if (textField.isOptional && value === '') {
          textField.invalid = false;
        } else if (textField.validationType === 'url') {
          textField.invalid = !isValidUrl(value);
        }
      }
    });
  };

  @action
  setFileData = (file: File | undefined, fileType: FileTypes): void => {
    this.fileData[fileType] = file ?? null;

    if (fileType === FileTypes.MEDIA) {
      this.mediaData.images = undefined;
      this.mediaData.video = undefined;
    } else if (fileType === FileTypes.LOGO) {
      this.mediaData.logo = undefined;
    }
  };

  loadMetadata = async (): Promise<void> => {
    const cabinetId = this.options.cabinetId.value;
    const cabinetValue = this.options.cabinet.value;
    const selectedCabinet = this.options.cabinet.items.find((item) => item.value === cabinetValue) as IOptionMyTarget;

    if (cabinetId === null || cabinetId === undefined) return;
    const response = await this.services.api.cabinetCopying.loadMetadata(cabinetId);

    runInAction(() => {
      if (response.data) {
        let responseData = response.data;
        const isSpecialCabinet = selectedCabinet && selectedCabinet.isMyTarget;

        responseData = isSpecialCabinet
          ? response.data
              .filter((opt) => opt.name !== 'campaign')
              .map((opt) => (opt.name === 'adGroup' ? { ...opt, name: 'adGroupMyTarget' } : opt))
          : response.data;

        this.optionNames = optionNamesByPriority.filter((option) => responseData.find(({ name }) => name === option));
        this.textFields = responseData
          .filter(({ format }) => format !== 'list')
          .map(({ name, format, isOptional }) => ({
            name,
            value: '',
            isOptional,
            invalid: false,
            validationType: format !== 'list' ? format : undefined, // NOTE: other validation types may be added
          }));
      }
    });
  };

  @action
  loadOptionItems = async (optionName: OptionName): Promise<void> => {
    const optionIndex = this.optionNames.findIndex((name) => name === optionName);
    const restOptionNames: OptionName[] = this.optionNames.slice(optionIndex);
    const optionToBeLoaded =
      restOptionNames[0] === 'client' && this.isClientOptional ? restOptionNames[1] : restOptionNames[0];

    restOptionNames.forEach(this.resetOption);

    if (optionToBeLoaded) {
      await this.loadOption(optionToBeLoaded);
    }
  };

  loadTemplateData = async (): Promise<void> => {
    const response = await this.services.api.cabinetCopying.loadTemplateData();

    runInAction(() => {
      this.templateData = response.data ?? [];
    });
  };

  downloadTemplate = async (): Promise<void> => {
    if (this.templateFileId === null) return;

    this.services.api.file.download({ fileId: this.templateFileId, fileType: 'ACCOUNT_COPYING' });
  };

  save = async (): Promise<void> => {
    if (this.fileData.EXCEL === null) return;

    try {
      await this.updateMediaData();

      const selectedCabinet = this.options.cabinet.items.find((item) => item.value === this.options.cabinet.value) as
        | (IOption & CabinetProps)
        | undefined;
      const cabinetId = selectedCabinet?.cabinetId ?? this.optionValues.cabinetId;

      const optionValues = {
        cabinetId,
        cabinetAccountCopyingId: this.optionValues.cabinet,
        accountId: this.optionValues.account,
        clientId: this.optionValues.client,
        campaignId: this.optionValues.campaign,
        groupId: this.optionValues.adGroup,
        adsId: this.optionValues.ad,
      };
      const textValues = this.textFields.reduce(
        (acc, { name, value, validationType }) => ({
          ...acc,
          [name]: validationType === 'url' && value ? new URL(encodeUrlParams(value)).href : value,
        }),
        {},
      );
      const data = { ...optionValues, ...textValues, mediaData: this.mediaData };

      const response = await this.services.api.cabinetCopying.uploadExcel(this.fileData.EXCEL, data);

      runInAction(() => {
        if (response.error) return;

        toast.success(localize('messages.file-uploaded-successfully'));
        this.setIsPanelActive(false);
      });
    } catch (error) {
      const label =
        localize(`cabinet-copying.uploader/${(error as IError).description?.toLowerCase()}` as LocaleIdType) ??
        localize('cabinet-copying.uploader.media-file');
      const introMsg = `${localize('cabinet-copying.uploader.failed-to-upload')} ${label}`;
      handleError(error as IError, null, introMsg);
    }
  };

  @action
  private loadOption = async (optionName: OptionName): Promise<void> => {
    this.abortController?.abort();
    this.abortController = ['campaign', 'adGroup', 'adGroupMyTarget', 'ad'].includes(optionName)
      ? new AbortController()
      : null;

    const { cabinetCopying } = this.services.api;
    const optionNameToLoadingFnMap = {
      cabinet: cabinetCopying.loadCabinets,
      cabinetId: cabinetCopying.loadCabinets,
      account: cabinetCopying.loadAccounts,
      client: cabinetCopying.loadClients,
      campaign: cabinetCopying.loadCampaigns,
      adGroup: cabinetCopying.loadAdGroups,
      adGroupMyTarget: cabinetCopying.loadAdGroups,
      ad: cabinetCopying.loadAds,
    };

    const option = this.options[optionName];
    option.requestCount += 1;

    const response = await optionNameToLoadingFnMap[optionName](this.optionValues, {
      withLoader: false,
      signal: this.abortController?.signal,
    });

    runInAction(() => {
      option.items = response.data ?? [];
      option.requestCount -= 1;

      if (option.items.length === 1) {
        this.setOption(optionName, option.items[0].value);
      }

      if (response.error?.message !== 'canceled') {
        option.disabled = false;
      }

      if (optionName === 'ad') {
        const isPlaceholderEmpty =
          option.items.length === 1 &&
          typeof option.items[0].label === 'string' &&
          (option.items[0].label.trim() === '' || option.items[0].label.trim().toLowerCase() === 'empty');

        if (option.items.length === 0 || isPlaceholderEmpty) {
          this.optionNames = this.optionNames.filter((name) => name !== 'ad');
        } else if (!this.optionNames.includes('ad')) {
          this.optionNames = optionNamesByPriority.filter((name) => this.optionNames.includes(name) || name === 'ad');
        }
      }
    });
  };

  @action
  private resetOption = (optionName: OptionName): void => {
    const option = this.options[optionName];
    option.items = [];
    option.value = null;
    option.disabled = true;
  };

  private updateMediaData = async (): Promise<void> => {
    if (this.needMediaFile && this.fileData.MEDIA) {
      const fileType = getFileType(this.fileData.MEDIA);
      if (fileType === MediaTypes.IMAGE && !this.mediaData.images) {
        this.mediaData.images = await this.getUploadedImageData(this.fileData.MEDIA);
      } else if (fileType === MediaTypes.VIDEO && !this.mediaData.video) {
        this.mediaData.video = await this.getUploadedVideoData(this.fileData.MEDIA);
      }
    }
    if (this.needLogoFile && this.fileData.LOGO && !this.mediaData.logo) {
      this.mediaData.logo = await this.getUploadedLogoData(this.fileData.LOGO);
    }
  };

  private getUploadedImageData = async (file: File): Promise<string[]> => {
    const response = await this.services.api.cabinetCopying.uploadPhoto(file, this.advertisement?.adFormat, {
      withErrorNotification: false,
    });

    return runInAction(() => {
      if (response.error) {
        response.error.description = MediaTypes.IMAGE;
        throw response.error;
      }
      return [response.data.url];
    });
  };

  private getUploadedVideoData = async (file: File): Promise<string> => {
    const response = await this.services.api.cabinetCopying.uploadVideo(file, { withErrorNotification: false });

    return runInAction(() => {
      if (response.error) {
        response.error.description = MediaTypes.VIDEO;
        throw response.error;
      }
      return response.data.url;
    });
  };

  private getUploadedLogoData = async (file: File): Promise<string> => {
    const response = await this.services.api.cabinetCopying.uploadLogo(file, { withErrorNotification: false });

    return runInAction(() => {
      if (response.error) {
        response.error.description = MediaTypes.LOGO;
        throw response.error;
      }
      return response.data.url;
    });
  };
}
